import { useCallback, useContext, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  Grid,
  TextField,
  IconButton,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';

import { useNavigate } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfile';
import { FormattedMessage } from 'react-intl';
import { AccountSetting } from '../../account-setting/components/AccountSetting';
import { DeleteAccount } from '../../account-setting/components/DeleteAccount';
import { CommunityWithdraw } from '../../community/components/CommunityWithdraw';
import {
  Routes,
  useUserInfoContext,
  useAnalytics,
  METRICS_COMMUNITY_EDIT_PERSONAL_INFO,
  METRICS_COMMUNITY_WITHDRAW_MENU_CLICK,
  METRICS_COMMUNITY_WITHDRAW_MENU_ITEM_CLICK,
} from '@xolv-mybrightlink/common';
import { PatientProfile } from './PatientProfile';

export function ProfilePage() {
  const [profile] = useContext(UserProfileContext);
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const [userInfo, dispatchUserInfo] = useUserInfoContext();
  const [showMore, setShowMore] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const openShowMore = useCallback(() => {
    setShowMore(true);
    analytics.trackAction(METRICS_COMMUNITY_WITHDRAW_MENU_CLICK);
  }, [analytics]);

  const openWithdrawPage = useCallback(() => {
    setShowMore(false);
    setShowWithdraw(true);
    analytics.trackAction(METRICS_COMMUNITY_WITHDRAW_MENU_ITEM_CLICK);
  }, [analytics]);

  const onWithdrawn = useCallback(() => {
    setShowWithdraw(false);
    dispatchUserInfo({ type: 'COMMUNITY_OPT_OUT' });
  }, [dispatchUserInfo]);

  if (showWithdraw) {
    return (
      <CommunityWithdraw
        onBack={() => setShowWithdraw(false)}
        onWithdrawn={onWithdrawn}
      />
    );
  }

  const patients = profile?.patients ?? [];
  return (
    <Box sx={{ px: 10, pt: 10, height: '100%' }}>
      <Typography
        variant='h6'
        sx={{
          fontWeight: '500',
        }}
      >
        <FormattedMessage id='account.title' />
      </Typography>

      <Box sx={{ flexGrow: 1, mt: 12.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant='subtitle1'
              sx={{ color: '#3b4559', fontWeight: '500' }}
            >
              <FormattedMessage id='profile.title' />
            </Typography>
          </Grid>
          <Grid item xs={4} />
          <Grid item xs={8}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
                {profile?.fullName || ''}
              </Typography>

              {!showMore && (
                <Box>
                  <Button
                    sx={{ py: 2.5, px: 10, mr: 5, borderRadius: 22 }}
                    variant='contained'
                    onClick={() => {
                      analytics.trackAction(
                        METRICS_COMMUNITY_EDIT_PERSONAL_INFO,
                      );
                      navigate(Routes.profile.basic);
                    }}
                  >
                    <FormattedMessage id='common.edit' />
                  </Button>
                  {userInfo.community_opt_in && (
                    <IconButton
                      sx={{ py: 2.5, width: 40, borderRadius: 3 }}
                      onClick={openShowMore}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} color='#898f9b' />
                    </IconButton>
                  )}
                </Box>
              )}

              {showMore && (
                <Box>
                  <Button
                    sx={{ py: 2.5, px: 10, mr: 5, borderRadius: 22 }}
                    variant='outlined'
                    color='error'
                    onClick={openWithdrawPage}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      <FormattedMessage id='community.withdraw' />
                    </Typography>
                  </Button>
                  <IconButton onClick={() => setShowMore(false)}>
                    <CancelIcon sx={{ color: '#898f9b' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Typography
              variant='body1'
              sx={{ mt: 5, color: '#307fe2', fontWeight: 'bold' }}
            >
              <FormattedMessage id='profile.client.title' />
            </Typography>

            <TextField
              variant='standard'
              multiline
              InputProps={{ disableUnderline: true, readOnly: true }}
              sx={{
                width: 1,
                mt: 2.5,
                color: '#3b4559',
              }}
              value={profile?.biography ?? ''}
            ></TextField>

            <Typography
              variant='body1'
              sx={{ mt: 5, color: '#307fe2', fontWeight: 'bold' }}
            >
              <FormattedMessage id='profile.client.interest' />
            </Typography>

            <Box sx={{ mt: 3 }}>
              {profile?.interests?.length ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {(profile?.interests || []).map((item: string) => (
                    <li key={item}>
                      <Box sx={{ mt: 2.5 }}>
                        <FormattedMessage id={`profile.interests.${item}`} />
                      </Box>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant='body1' sx={{ color: '#3b4559' }}>
                  <FormattedMessage id='profile.interests.none' />
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={8}>
            <Typography
              variant='body1'
              sx={{ mt: 5, color: '#307fe2', fontWeight: 'bold' }}
            >
              <FormattedMessage id='profile.children.title' />
            </Typography>

            <Box sx={{ mt: 3, pb: 3, overflowY: 'auto' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                {patients.map((patient) => (
                  <PatientProfile
                    key={patient.guid}
                    patient={patient}
                    fullWidth={patients.length === 1}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 6 }} />
      <AccountSetting />
      <Divider sx={{ my: 6 }} />
      <DeleteAccount />
    </Box>
  );
}
